import { Component, OnChanges, SimpleChanges, Input, Output, forwardRef, ViewChild, EventEmitter } from '@angular/core';
import { CampusUnit } from '../../models';
import { SortService } from '../../services';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'app-campus-unit-select',
  templateUrl: './campus-unit-select.component.html',
  styleUrls: ['./campus-unit-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CampusUnitSelectComponent),
      multi: true
    }
  ]
})
export class CampusUnitSelectComponent implements OnChanges, ControlValueAccessor {
  @ViewChild('autoCompleteInput', { static: true, read: MatAutocompleteTrigger }) autoCompleteTrigger: MatAutocompleteTrigger;

  @Input() units: CampusUnit[];
  @Input() placeholder: string;

  @Output() unitSelected: EventEmitter<CampusUnit> = new EventEmitter();

  filteredUnits: CampusUnit[];
  _selectedUnit: CampusUnit;

  get selectedUnit() {
    return this._selectedUnit;
  }

  set selectedUnit(value) {
    this._selectedUnit = value;
    this.propagateChange(this._selectedUnit);
  }

  disabled = false;

  // an empty function for handling on change notification
  propagateChange = (_: any) => {};

  constructor(private sorter: SortService) {}

  // -- Interface Methods
  ngOnChanges(changes: SimpleChanges) {
    this.selectedUnit = null;
    this.onUnitChange();
  }

  writeValue(value: any): void {
    this.selectedUnit = value;
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    // don't need to do anything here
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // -- End Interface Methods

  /**
   * Clears the selected unit.
   */
  clear() {
    this.selectedUnit = null;
    // have to have a set timeout or else this doesn't actually display the panel; would be nice to know why
    setTimeout(() => {
      if (this.autoCompleteTrigger) {
        this.autoCompleteTrigger.openPanel();
      }});
  }

  /**
   * Gets what should be displayed when a campus ward is selected.
   */
  getUnitDisplay(campusUnit: CampusUnit): string {
    return campusUnit ? campusUnit.name : '';
  }

  /**
   * Filters our list of units, using a contains on the name.
   *
   * We have to do an any on our type because as the user types it will be a string, but once selected it's the whole object.
   */
  onUnitChange(unitName?: any) {
    if (!this.units) {
      return;
    }

    // don't love that I'm doing a type check, but when the user types it's a string and after making selection we get called again with the real object
    if (unitName && typeof unitName === 'string') {
      const unitNameNoCase = unitName.toLowerCase();
      this.filteredUnits = this.units.filter(unit => {
        return unit.name.toLowerCase().indexOf(unitNameNoCase) >= 0;
      });
    } else {
      this.filteredUnits = this.units.slice(0);
    }

    this.filteredUnits.sort(this.sorter.sortChurchUnitsAsc);
  }

  /**
   * When a unit is selected, emit an event with the unit.
   */
  onUnitSelected() {
    this.unitSelected.emit(this.selectedUnit);
  }
}
